import useOnMount from '@/hooks/useOnMount';
import { Button } from '@cmpsr/components';
import { useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { PaymentFormState } from '../PPRAPaymentPage';
import { StickyPurchaseButton } from './StickyPurchaseButton';
import { Box, Text } from '@cmpsr/components';
import { VARS } from '../../common/constants';
import { formatCurrency } from '@/utils';
import { GreenCheckmark } from '../../common/GreenCheckmark';
import customTheme from '@/constants/theme.custom';

const termsStyle = {
  variant: 'text-body-meta-regular' as const,
  ml: '0.250rem',
  color: 'darkBlue',
  fontSize: '12px',
  display: 'block',
  lineHeight: '140%',
};

interface PurchaseSectionProps {
  form: UseFormReturn<PaymentFormState>;
  isPaymentComplete: boolean;
  currentPrice: number | null;
  setShowPaymentError: (value: boolean) => void;
  paymentSectionRef: React.RefObject<HTMLDivElement>;
  handleSubmit: () => void;
  isSubmitting: boolean;
  isTrial?: boolean;
  priceRecurring?: {
    interval?: string;
    intervalCount?: number;
    amount?: number;
  };
}

const PurchaseSection = ({
  form,
  isPaymentComplete,
  setShowPaymentError,
  paymentSectionRef,
  handleSubmit,
  isSubmitting,
  isTrial,
  priceRecurring,
}: PurchaseSectionProps) => {
  const mainButtonRef = useRef<HTMLButtonElement>(null);
  const [showStickyButton, setShowStickyButton] = useState(false);

  const handlePurchase = () => {
    const callback = async () => {
      await form.trigger();

      // If payment is not complete, show payment error
      if (!isPaymentComplete) {
        setShowPaymentError(true);
      }

      // If form is not valid or payment is not complete, scroll to payment section and trigger validation
      if (!form.formState.isValid || !isPaymentComplete) {
        form.trigger(); // Trigger form validation to show errors
        paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Otherwise process the purchase using the controller's handleSubmit
      handleSubmit();
    };

    callback();
  };

  // Effect to handle the sticky button
  useOnMount(() => {
    const checkMainButtonVisibility = () => {
      if (mainButtonRef.current) {
        const mainButtonPosition = mainButtonRef.current.getBoundingClientRect();
        const isScrolledPast = mainButtonPosition.bottom < 0;
        setShowStickyButton(isScrolledPast);
      }
    };

    window.addEventListener('scroll', checkMainButtonVisibility);
    window.addEventListener('resize', checkMainButtonVisibility);

    // Initial check
    checkMainButtonVisibility();

    return () => {
      window.removeEventListener('scroll', checkMainButtonVisibility);
      window.removeEventListener('resize', checkMainButtonVisibility);
    };
  });

  const buttonText = isTrial ? 'Start Your 7 Day Free Trial' : VARS.cta.message;
  const yearSuffix = priceRecurring?.interval === 'year' ? '/year' : '';
  const priceAmount = priceRecurring?.amount ? `$${(priceRecurring.amount / 100).toFixed(0)}` : '$119';

  return (
    <>
      <Button
        ref={mainButtonRef}
        width="100%"
        height="50px"
        backgroundColor="#7cf1c7"
        textColor="darkBlue"
        borderRadius="9999px"
        fontSize="18px"
        fontWeight="bold"
        onClick={handlePurchase}
        isLoading={isSubmitting}
      >
        {buttonText}
      </Button>
      {isTrial && (
        <Box paddingTop={4} paddingX={4} textAlign="center" display="flex" alignItems="center" justifyContent="center" gap="8px">
          <GreenCheckmark />
          <Text
            fontSize="14px"
            fontWeight="700"
            color="#183344"
            lineHeight="140%"
            fontFamily={customTheme.typography.fonts.inter}
          >
            7 day free trial, then {priceAmount}{yearSuffix}. Cancel anytime
          </Text>
        </Box>
      )}
      <Box paddingTop={6} paddingX={4} textAlign="center">
        <Text {...termsStyle}>
          By joining Catch, you agree to our{' '}
          <a
            style={{ textDecoration: 'underline' }}
            href="https://www.catchbio.com/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            style={{ textDecoration: 'underline' }}
            href="https://www.catchbio.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </Text>
      </Box>
      {showStickyButton && <StickyPurchaseButton onClick={handlePurchase} isLoading={isSubmitting} isTrial={isTrial} />}
    </>
  );
};

export default PurchaseSection;
